import { IconButton } from '@ui/shared/Button';
import * as React from 'react';
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableProvidedDraggableProps,
  DraggableProvidedDragHandleProps,
  Droppable,
  DroppableProvided,
  DropResult,
} from 'react-beautiful-dnd';
import styled from 'styled-components';
import {
  ICrossTestMetricSelection,
  ISelectableEntity,
  ISelectableMetric,
  ITag,
} from '../../../../data/types';
import { queryHashGenerator } from '../../../../utils/queryHash';
import { testMetaString } from '../../../../utils/testMetaString';
import { Input } from '../../../shared/form/Input';
import { Icon } from '../../../shared/Icon';
import {
  ListItem,
  ListItemCell,
  ListItemSafeText,
} from '../../../shared/List/ListItem';
import { SecondaryText } from '../../../shared/SecondaryText';
import { Row } from '../../../layout/Row';

interface IProps {
  useCutoffs?: boolean;
  unsignedCutoffs?: boolean;
  isBlockReport?: boolean;
  metrics: Array<ICrossTestMetricSelection | ISelectableMetric>;
  onReorder?: (
    metrics: Array<ICrossTestMetricSelection | ISelectableMetric>
  ) => void;
  onRemoveSelection: (
    selection: ICrossTestMetricSelection | ISelectableMetric
  ) => void;
  onLowerCutoffChange?: (
    selection: ICrossTestMetricSelection | ISelectableMetric
  ) => void;
  onUpperCutoffChange?: (
    selection: ICrossTestMetricSelection | ISelectableMetric
  ) => void;
}

export const MetricList = (props: IProps): React.ReactElement => {
  const listShape = [2, 1];

  /**
   * @description reorder list
   * @param result
   */
  const onReorder = (result: DropResult): void => {
    const list = props.metrics;
    const newList = Array.from(list);
    const [removed] = newList.splice(result.source.index, 1);
    newList.splice(result.destination.index, 0, removed);
    props?.onReorder(newList);
  };

  return (
    <DragDropContext onDragEnd={onReorder}>
      <Droppable droppableId='droppable'>
        {(droppableProvided: DroppableProvided): React.ReactElement => (
          <div
            {...droppableProvided.droppableProps}
            ref={droppableProvided.innerRef}
          >
            {props.metrics.map(
              (
                km: ICrossTestMetricSelection | ISelectableMetric,
                index: number
              ): React.ReactElement => {
                const key = makeKey(km);
                return (
                  <Draggable key={key} draggableId={key} index={index}>
                    {(
                      draggableProvided: DraggableProvided
                    ): React.ReactElement => (
                      <MetricListItem
                        useCutoffs={props.useCutoffs}
                        innerRef={draggableProvided.innerRef}
                        draggableHandleProps={draggableProvided.dragHandleProps}
                        draggableProps={draggableProvided.draggableProps}
                        selection={km}
                        shape={listShape}
                        unsignedCutOffs={props.unsignedCutoffs}
                        onRemove={props.onRemoveSelection}
                        onLowerCutoffChange={props.onLowerCutoffChange}
                        onUpperCutoffChange={props.onUpperCutoffChange}
                      />
                    )}
                  </Draggable>
                );
              }
            )}
            {droppableProvided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

const makeKey = (
  selection: ICrossTestMetricSelection | ISelectableMetric
): string => {
  if ((selection as ICrossTestMetricSelection)?.testType) {
    const s = selection as ICrossTestMetricSelection;
    const queryHash = queryHashGenerator(
      s.testType.value,
      s.tags.map((t: ISelectableEntity): string => t.value)
    );
    return `${s.metric.value}_${queryHash}`;
  } else {
    const s = selection as ISelectableMetric;
    return s.value;
  }
};

interface IItemProps {
  useCutoffs?: boolean;
  unsignedCutOffs?: boolean;
  isBlockReport?: boolean;
  draggableHandleProps: DraggableProvidedDragHandleProps;
  draggableProps: DraggableProvidedDraggableProps;
  innerRef: React.Ref<HTMLDivElement>;
  selection: ICrossTestMetricSelection | ISelectableMetric;
  shape: number[];
  style?: React.CSSProperties;
  onRemove: (c: ICrossTestMetricSelection | ISelectableMetric) => void;
  onLowerCutoffChange?: (
    c: ICrossTestMetricSelection | ISelectableMetric
  ) => void;
  onUpperCutoffChange?: (
    c: ICrossTestMetricSelection | ISelectableMetric
  ) => void;
}

export const MetricListItem = (props: IItemProps): React.ReactElement => {
  /**
   * @description create the test type string from the test type and tags
   */
  let typeTypeString = '';
  if ((props.selection as ICrossTestMetricSelection).testType !== undefined) {
    typeTypeString = testMetaString(
      (props.selection as ICrossTestMetricSelection)?.testType.entity.name,
      (props.selection as ICrossTestMetricSelection)?.tags?.map(
        (t: ISelectableEntity): ITag => t.entity as ITag
      ) ?? []
    );
  }
  const metric =
    (props.selection as ICrossTestMetricSelection)?.metric ??
    (props.selection as ISelectableMetric);

  const removeHandler = (): void => {
    props.onRemove(props.selection);
  };

  /**
   * @description handles changes to the lower % change value
   * @param event
   */
  const lowerCutoffChangeHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const { value } = e.target;
    const selection = props.selection as ICrossTestMetricSelection;
    props.onLowerCutoffChange({
      ...selection,
      changeCutoffs: {
        ...selection.changeCutoffs,
        lowerCutoff: isNaN(+value)
          ? selection.changeCutoffs.upperCutoff
          : +value,
      },
    });
  };

  /**
   * @description handles changes to the upper % change value
   * @param event
   */
  const upperCutoffChangeHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const { value } = e.target;
    const selection = props.selection as ICrossTestMetricSelection;
    props.onUpperCutoffChange({
      ...selection,
      changeCutoffs: {
        ...selection.changeCutoffs,
        upperCutoff: isNaN(+value)
          ? selection.changeCutoffs.lowerCutoff
          : +value,
      },
    });
  };

  const inputBackground = (inputFor: 'lower' | 'upper'): string => {
    if (props.unsignedCutOffs) {
      if (inputFor === 'lower') {
        return '#e1f43640';
      } else {
        return '#F4433620';
      }
    } else {
      if (inputFor === 'lower') {
        return metric.entity.direction === -1 ? '#4CAF5020' : '#F4433620';
      } else if (inputFor === 'upper') {
        return metric.entity.direction === 1 || metric.entity.direction === 0
          ? '#4CAF5020'
          : '#F4433620';
      }
    }
  };

  return (
    <DraggableListItemContainer
      ref={props.innerRef}
      {...props.draggableHandleProps}
      {...props.draggableProps}
    >
      <ListItem style={props.style}>
        <ListItemCell
          style={{
            alignItems: 'center',
            flex: 3,
            flexDirection: 'row',
          }}
        >
          <div>
            <ListItemSafeText
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              {metric.label}
              {''} {metric.entity.direction === -1 && <Icon name='south' />}
            </ListItemSafeText>
            <ListItemSafeText>
              <SecondaryText>{typeTypeString}</SecondaryText>
            </ListItemSafeText>
          </div>
        </ListItemCell>

        {props.useCutoffs && (
          <ListItemCell
            style={{
              alignItems: 'center',
              display: 'flex',
              flex: 2,
              flexDirection: 'row',
              justifyContent: 'flex-end',
            }}
          >
            {metric.entity.direction === -1 && props.isBlockReport && (
              <Icon name='south' />
            )}
            <Input
              minWidth='10px'
              style={{
                background: inputBackground('lower'),
                margin: '0 5px',
                width: '50px',
              }}
              placeholder={
                (
                  props.selection as ICrossTestMetricSelection
                ).changeCutoffs?.lowerCutoff?.toString() + '%'
              }
              type='text'
              onChange={lowerCutoffChangeHandler}
            />
            <Input
              minWidth='10px'
              style={{
                background: inputBackground('upper'),
                margin: '0 5px',
                width: '50px',
              }}
              placeholder={
                (
                  props.selection as ICrossTestMetricSelection
                ).changeCutoffs?.upperCutoff?.toString() + '%'
              }
              type='text'
              onChange={upperCutoffChangeHandler}
            />
          </ListItemCell>
        )}
        <ListItemCell style={{ flex: 0.25 }}>
          <IconButton icon='clear' onClick={removeHandler} title='Remove' />
        </ListItemCell>
      </ListItem>
    </DraggableListItemContainer>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DraggableListItemContainer = styled.div`
  border-bottom-width: 2px;
  border-bottom-style: dashed;
  border-bottom-color: var(--color-separator);
  height: 60px;
  &:last-child {
    border-bottom-color: transparent;
  }
`;
